import { Address as AssetAddress, OrganizationResponse, Person as PersonAsset } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { Currency, Address as OrderAddress, Person as PersonOrder } from '@cdaas/api-clients/dist/order-admin-openapi';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';
import { getPriceIncludingVat, getVatAmount } from '@cdaas/reusable-components-daas';
import {
  CreateOrderLine,
  CreateProductOrder,
  DeliveryDeliveryOption,
  OrderLineType,
  PaymentInfoPaymentTypeEnum,
  PriceEntry,
  PriceEntryCodeEnum,
} from '@cdaas/api-clients/dist/order-openapi';
import {
  BundlePrice,
  BundlePriceTypeEnum,
  LocalBundleItem,
  LocalProductTypeEnum,
  LocalProductVariant,
  Material,
  SalesChannel,
} from '@cdaas/api-clients/dist/product-service-openapi';
import { isNumber } from 'lodash';

import { UserResponseWithUserGroup, getUserFromUserResponse } from '../../../utils';

export interface OrderLineValues {
  name?: string;
  brand?: string;
  stock_keeping_unit?: string;
  product_id?: string;
  leasing_id?: string;
  acquisitionPrice?: number;
  location_reference?: string;
}

export const MISSING_ENROLLMENT_ID_ERROR_MESSAGE = 'MISSING_ENROLLMENT_ID';

const convertAssetPersonToOrderPerson = (person?: PersonAsset | null): PersonOrder | undefined => {
  if (person?.email && person?.first_name && person?.last_name && person?.country_calling_code && person?.phone_number) {
    return {
      email: person.email,
      given_name: person.first_name,
      family_name: person.last_name,
      country_calling_code: person.country_calling_code,
      phone_number: person.phone_number,
    };
  }
  return undefined;
};

const convertAssetAddressToOrderAddress = (address?: AssetAddress | null): OrderAddress | undefined => {
  if (address?.street && address?.zip && address?.city && address?.country) {
    return {
      zip: address.zip,
      city: address.city,
      country: address.country,
      address_1: address.street,
      address_2: address.address1 ?? undefined,
      address_3: address.address2 ?? undefined,
    };
  }
  return undefined;
};

const isValidAssetPerson = (person: PersonAsset): boolean =>
  !!person?.email && !!person?.first_name && !!person?.last_name && !!person?.country_calling_code && !!person?.phone_number;

const isAddressValid = (address?: AssetAddress | null): boolean => {
  return !!address?.street && !!address?.city && !!address?.zip && !!address?.country;
};

export const isAllInformationAvailableForOrdering = (
  organization: OrganizationResponse | null,
  selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null,
  salesChannel: SalesChannel | null,
  person?: PersonAsset | null,
  user?: UserResponseWithUserGroup | null,
  deliveryAddress?: AssetAddress | null,
  materials?: Material[],
): boolean => {
  return (
    !!person &&
    (!!user?.id || user === null) &&
    !!organization?.id &&
    !!organization?.organization_number &&
    isValidAssetPerson(person) &&
    isAddressValid(deliveryAddress) &&
    !!selectedProduct?.bundle.prices?.length &&
    !!materials?.length &&
    !!salesChannel
  );
};

const isEnrollmentBundle = (product: LocalBundleEnvelopeWithChosenMainVariant): boolean =>
  product.bundle.items.some(
    item => item.options.some(option => option.sku === '5011397'), // as in products-service
  );

const isMainItemVariant = (material: Material, selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null) =>
  selectedProduct?.bundle.main_item.variants?.some(variant => variant.id === material.variant_id) || false;

const isMainProduct = (material: Material, selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null) =>
  material.variant_id === selectedProduct?.chosenMainItemVariantId;

export const isSelectedMaterial = (material: Material, selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null) =>
  !isMainItemVariant(material, selectedProduct) || isMainProduct(material, selectedProduct);

export const isHardware = (material: Material, selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null) => {
  const bundleMainItemType = selectedProduct?.bundle.main_item.type;
  const isHardwareTypeMainItemVariant = bundleMainItemType === LocalProductTypeEnum.Hardware && isMainItemVariant(material, selectedProduct);
  const isHardwareTypeAddonProduct = selectedProduct?.bundle.items.some(item =>
    item.options.some(
      option =>
        option.id === material.variant_id &&
        selectedProduct.addon_products?.some(
          addonProduct => addonProduct.id === option.product_id && addonProduct.type === LocalProductTypeEnum.Hardware,
        ),
    ),
  );
  return isHardwareTypeMainItemVariant || isHardwareTypeAddonProduct || false;
};

export const checkIsEnrollmentIdIsRequired = (product: LocalBundleEnvelopeWithChosenMainVariant, organization: OrganizationResponse): void => {
  const isAppleBundle = product.bundle.main_item.brand.toLowerCase() === 'apple';
  const isSamsungBundle = product.bundle.main_item.brand.toLowerCase() === 'samsung';

  if (isEnrollmentBundle(product)) {
    if (isAppleBundle && !organization.apple_enrollment_id) {
      throw new Error(MISSING_ENROLLMENT_ID_ERROR_MESSAGE);
    }

    if (isSamsungBundle && !organization.samsung_enrollment_id) {
      throw new Error(MISSING_ENROLLMENT_ID_ERROR_MESSAGE);
    }
  }
};

const getActiveAcquisitionPriceForProductVariant = (localProductVariant: LocalProductVariant, salesChannel: SalesChannel) => {
  const salesChannelAcquisitionPrice = localProductVariant.sales_channel_acquisition_prices.find(
    acquisitionPrice => acquisitionPrice.sales_channel_id === salesChannel.id,
  )?.amount;
  const countryAcquisitionPrice = localProductVariant.country_acquisition_prices.find(
    acquisitionPrice => acquisitionPrice.country_id === salesChannel.country_id,
  )?.amount;
  if (isNumber(salesChannelAcquisitionPrice)) {
    return salesChannelAcquisitionPrice;
  } else if (isNumber(countryAcquisitionPrice)) {
    return countryAcquisitionPrice;
  }
};

export const getCorrectBundleItemOptionValuesForAddon = (
  selectedProduct: LocalBundleEnvelopeWithChosenMainVariant,
  bundleItemOption: LocalProductVariant,
  bundleItem: LocalBundleItem,
  salesChannel: SalesChannel,
): OrderLineValues => {
  const name = bundleItem.name;
  const product_id = bundleItem.id.toString();
  const stock_keeping_unit = bundleItemOption.sku;
  const acquisitionPrice = salesChannel.use_automatic_lease_price
    ? getActiveAcquisitionPriceForProductVariant(bundleItemOption, salesChannel)
    : undefined;
  let brand;

  if (bundleItemOption.product_id) {
    const option = selectedProduct.addon_products?.find(addonProduct => addonProduct.id === bundleItemOption.product_id);
    if (option?.id) {
      const addonProduct = selectedProduct!.addon_products?.find(addonProduct => addonProduct.id === option?.id);
      brand = addonProduct?.brand;
    }
  }
  return { name, product_id, stock_keeping_unit, acquisitionPrice, brand };
};

export const getCorrectBundleItemOptionValuesForMainItem = (
  selectedProduct: LocalBundleEnvelopeWithChosenMainVariant,
  salesChannel: SalesChannel,
  locationReference?: string,
): OrderLineValues => {
  const bundleMainItemVariant = selectedProduct!.bundle.main_item.variants?.find(
    variant => variant.product_id === selectedProduct!.bundle.main_item.id,
  )!;

  return {
    name: selectedProduct!.bundle.main_item.name,
    brand: selectedProduct!.bundle.main_item.brand,
    product_id: selectedProduct!.bundle.main_item.id?.toString(),
    acquisitionPrice: salesChannel.use_automatic_lease_price
      ? getActiveAcquisitionPriceForProductVariant(bundleMainItemVariant, salesChannel)
      : undefined,
    stock_keeping_unit: bundleMainItemVariant.sku,
    leasing_id: salesChannel.use_automatic_lease_price
      ? bundleMainItemVariant.finance_product_ids?.find(financeProductId => financeProductId.country_id === salesChannel.country_id)
          ?.finance_product_id
      : undefined,
    location_reference: locationReference,
  };
};

const getOrderLinePrice = (
  salesChannel: SalesChannel,
  currency: Currency,
  errorMessage: string,
  acquisitionPrice?: number,
): PriceEntry[] | undefined => {
  if (salesChannel.use_automatic_lease_price) {
    if (acquisitionPrice === null || acquisitionPrice === undefined) throw new Error(errorMessage);
    return [
      {
        code: PriceEntryCodeEnum.TeliaFinanceAcquisitionPrice,
        price: {
          amount: acquisitionPrice,
          total: acquisitionPrice,
          vat: 0,
          currency: currency.toUpperCase() as Currency,
        },
      },
    ];
  } else {
    return undefined;
  }
};

const getLeasingPeriodLength = (bundlePriceType: BundlePriceTypeEnum): number | undefined => Number(bundlePriceType.split('_')[1]) || undefined;

export const createOrderLine = (
  orderLineValues: OrderLineValues,
  selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null,
  material: Material,
  isMainProduct: boolean,
  salesChannel: SalesChannel,
  currency: Currency,
  errorMessage: string,
): CreateOrderLine => {
  if (!orderLineValues.name || !orderLineValues.product_id) {
    throw new Error(errorMessage);
  }
  return {
    product_id: orderLineValues.product_id!,
    external_id: material.variant_id.toString(),
    erp_material_code: material.code,
    type: isHardware(material, selectedProduct) ? OrderLineType.Hardware : OrderLineType.Service,
    brand: orderLineValues.brand,
    is_main_product: isMainProduct,
    name: orderLineValues.name!,
    stock_keeping_unit: orderLineValues.stock_keeping_unit,
    leasing_id: salesChannel.use_automatic_lease_price ? orderLineValues.leasing_id : undefined,
    prices: getOrderLinePrice(salesChannel, currency, errorMessage, orderLineValues.acquisitionPrice),
    location_reference: orderLineValues.location_reference,
  };
};

const getIsHomeDelivery = (selectedDelivery: DeliveryDeliveryOption, isHomeDeliverySelected: boolean, invalidDeliveryErrorMessage: string) => {
  if ((isHomeDeliverySelected && !selectedDelivery.home_delivery) || (!isHomeDeliverySelected && !selectedDelivery.business_parcel)) {
    throw new Error(invalidDeliveryErrorMessage);
  }
  return isHomeDeliverySelected;
};

export const createProductOrder = (
  organization: OrganizationResponse,
  deliveryContact: PersonAsset,
  deliveryAddress: AssetAddress,
  isHomeDeliverySelected: boolean,
  selectedDelivery: DeliveryDeliveryOption,
  invoicingAccountNumber: string | undefined,
  selectedProduct: LocalBundleEnvelopeWithChosenMainVariant,
  salesChannel: SalesChannel,
  bundlePrice: BundlePrice,
  selectedUser: UserResponseWithUserGroup | undefined,
  orderLines: CreateOrderLine[],
  invalidDeliveryErrorMessage: string,
): CreateProductOrder => {
  return {
    ordered_to: {
      external_organization_id: organization.id,
    },
    ship_to: {
      customer_name: organization.name,
      contact_person: convertAssetPersonToOrderPerson(deliveryContact),
      address: convertAssetAddressToOrderAddress(deliveryAddress)!,
    },
    delivery_info: {
      delivery_method_code: selectedDelivery.delivery_method_code,
      is_home_delivery: getIsHomeDelivery(selectedDelivery, isHomeDeliverySelected, invalidDeliveryErrorMessage),
    },
    call_off_number: invoicingAccountNumber,
    packages: [
      {
        external_id: selectedProduct.bundle.id.toString(),
        name: selectedProduct.bundle.name,
        leasing_package_id: salesChannel.use_automatic_lease_price ? undefined : selectedProduct.bundle.finance_id,
        leasing_period: getLeasingPeriodLength(bundlePrice.type),
        end_user: selectedUser ? getUserFromUserResponse(selectedUser)! : undefined,
        payment: {
          payment_type: PaymentInfoPaymentTypeEnum.Daas,
        },
        prices: [
          {
            code: PriceEntryCodeEnum.EndCustomerLeasePrice,
            price: {
              total: getPriceIncludingVat(bundlePrice.amount, bundlePrice.vat_percentage),
              amount: bundlePrice.amount,
              vat: getVatAmount(bundlePrice.amount, bundlePrice.vat_percentage),
              currency: organization.locale.currency.toUpperCase() as Currency,
            },
          },
        ],
        order_lines: orderLines,
        insured: bundlePrice.insured,
      },
    ],
  };
};
